import { useInfiniteQuery } from '@tanstack/react-query';

import { assetCartRequestUnavailableListSelector, auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { setPage as setPageAvailableList } from '@slices/assetCartRequestAvailableList';

import { getAssetCartRequestListShortView } from '@api/assetCartRequest';

import { TAllPages } from '@interfaces/common/defaultDTO';
import { IAssetCartRequestShortView } from '@interfaces/rent/assetRent';

import { refetchOptions } from '@constants/common';
import { ASSET_CART_REQUEST_UNAVAILABLE } from '@constants/queryKeys';

export const useQueryAssetCartRequestShortViewUnavailable = () => {
	const authState = useAppSelector(auth);

	const {
		accessPermission:
			{
				networkOperatorAssetEnquirer,
				assetConsumerAssetEnquirer,
			},
	} = authState;

	const isEnabled = networkOperatorAssetEnquirer || assetConsumerAssetEnquirer;

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		assetOwnerId,
		assetType,
	} = useAppSelector(assetCartRequestUnavailableListSelector);

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isFetchingNextPage,
	} = useInfiniteQuery({
		queryKey: [
			ASSET_CART_REQUEST_UNAVAILABLE,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			assetOwnerId,
			assetType,
		],
		queryFn: ({ pageParam = 1 }) => getAssetCartRequestListShortView({
			page: pageParam,
			pageSize,
			sortDirection,
			sortFieldName,
			searchValue,
			assetOwnerId,
			assetTypeId: assetType,
			isAvailableInPeriod: false,
		}),
		getNextPageParam: (lastPage, allPages: TAllPages<IAssetCartRequestShortView>[]) => {
			const maxItemsCount = allPages[0].page.count;
			const itemsCount = allPages.reduce((acc, page) => acc + page.page.items.length, 0);

			if (itemsCount < maxItemsCount) {
				setPageAvailableList({ page: page + 1 });

				return page + 1;
			}

			return;
		},
		retry: 0,
		keepPreviousData: true,
		...refetchOptions,
		enabled: isEnabled,
	});

	return {
		assetCartRequestListUnavailable: data?.pages || [],
		unavailableAssetRequestCount: data?.pages[0]?.unavailable || 0,
		isLoadingAssetCartRequestUnavailable: isFetching || isFetchingNextPage,
		fetchNextPageUnavailableList: fetchNextPage,
		hasNextPageUnavailableList: hasNextPage,
	};
};
